import './kitStyles';
import React, { isValidElement } from 'react';
import { Icon } from './Icon';
import type { IconName } from './icons';

/**
 * WEB-3310 юнит 1 — кнопка кита, эталон `Button({ kind = 'secondary', size,
 * icon, loading, children, className, ...rest })` (`design/cabinet-rescin/kit.jsx`).
 *
 * Классы — как в эталоне: `btn <kind> <size> [icon] [is-loading] <className>`.
 * Значок у кнопки с текстом не рисуется (ПРАВИЛА.md К-5) — кроме текста в
 * `<span className="tx">`, который прячется на узком экране. Загрузка —
 * `aria-busy`, кнопка выключена, вместо значка капля `.spin`.
 * Отличие от эталона: загрузка выключает кнопку и при явном `disabled={false}`
 * (в эталоне `...rest` перебивал `disabled`).
 */
export type ButtonKind = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-fill';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  kind?: ButtonKind;
  size?: 'sm' | 'lg';
  icon?: IconName;
  loading?: boolean;
}

function isTx(children: React.ReactNode): boolean {
  return (
    isValidElement<{ className?: string }>(children) &&
    children.props.className === 'tx'
  );
}

export function Button({
  kind = 'secondary',
  size,
  icon,
  loading,
  children,
  className,
  ...rest
}: ButtonProps): React.ReactElement {
  const shownIcon = !children || isTx(children) ? icon : undefined;
  const classes = [
    'btn',
    kind,
    size,
    !children && shownIcon ? 'icon' : '',
    loading ? 'is-loading' : '',
    className,
  ];
  return (
    <button
      type="button"
      className={classes.filter(Boolean).join(' ')}
      aria-busy={loading || undefined}
      {...rest}
      disabled={rest.disabled || loading}
    >
      {loading ? (
        <span className="spin" aria-hidden="true" />
      ) : shownIcon ? (
        <Icon name={shownIcon} />
      ) : null}
      {children}
    </button>
  );
}
